import { LoadingButton } from '@mui/lab';
import { Box, Button, Grid } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { GeneralSelectMenu } from 'src/components/general';
import GeneralDriverTextFiled from 'src/components/general/GeneralDriverTextFiled';
import { FormProvider, RHFTextField } from 'src/components/hook-form';
import { TRANSACTION_EXCHANGE_CONFIG_LIST } from 'src/constants/queryKeys';
import { networkCreateTransactionExchangeConfig, networkUpdateTransactionExchangeConfig } from 'src/network/exchange';
import useAppInfoStore from 'src/store/appInfo';
import useFormsModalStore from 'src/store/forms';
import { generateSelectMenuItems } from 'src/utils';
import { getDefaultValues } from './defaultValues';

const ExchangeForm = () => {
  const { modal, closeModal } = useFormsModalStore();

  const { enqueueSnackbar } = useSnackbar();

  const queryClient = useQueryClient();

  const { appInfo } = useAppInfoStore();

  const { projectId } = useParams();

  const [sourceAmountTypes, setSourceAmountTypes] = useState([]);
  const [selectedSourceAmountType, setSelectedSourceAmountType] = useState('');

  const [destinationAmountTypes, setDestinationAmountTypes] = useState([]);
  const [selectedDestinationAmountType, setSelectedDestinationAmountType] = useState('');

  const exchangeConfigDetails = modal?.data;
  const defaultValues = getDefaultValues(exchangeConfigDetails);
  const isEditMode = exchangeConfigDetails;

  const methods = useForm({
    // resolver: yupResolver(schema), // todo schema
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  // ---------------------------------------------------------------

  const onSubmit = async (values, event) => {
    const PAYLOAD = {
      ...values,
      TransactionExchangeConfigApplicationID: +projectId,
      TransactionExchangeConfigSourceTransactionAmountTypeID: selectedSourceAmountType,
      TransactionExchangeConfigDestinationTransactionAmountTypeID: selectedDestinationAmountType,
      TransactionExchangeConfigRate: +values?.TransactionExchangeConfigRate,
    };

    try {
      if (isEditMode) {
        const res = await networkUpdateTransactionExchangeConfig(exchangeConfigDetails?.TransactionExchangeConfigID, PAYLOAD);
        if (res.data?.items?.ResultID !== -1) {
          enqueueSnackbar('ترازو موردنظر با موفقیت ویرایش شد');
          if (event.nativeEvent.submitter.name === 'save') {
            closeModal();
            reset();
            await queryClient.invalidateQueries([TRANSACTION_EXCHANGE_CONFIG_LIST]);
          }
        } else {
          enqueueSnackbar('خطایی رخ داد !', { variant: 'error' });
        }
      } else {
        const res = await networkCreateTransactionExchangeConfig(PAYLOAD);
        if (res.data?.items?.ResultID !== -1) {
          enqueueSnackbar('ترازو موردنظر با موفقیت ایجاد شد');
          if (event.nativeEvent.submitter.name === 'save') {
            closeModal();
          }
          reset();
          await queryClient.invalidateQueries([TRANSACTION_EXCHANGE_CONFIG_LIST]);
        } else {
          enqueueSnackbar('خطایی رخ داد !', { variant: 'error' });
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    const amountTypeList = generateSelectMenuItems(appInfo?.AmountType, 'TransactionAmountTypeLable', 'TransactionAmountTypeID');

    setSourceAmountTypes(amountTypeList);
    setSelectedSourceAmountType(exchangeConfigDetails?.TransactionExchangeConfigSourceTransactionAmountTypeID || amountTypeList[0]?.id);

    setDestinationAmountTypes(amountTypeList);
    setSelectedDestinationAmountType(
      exchangeConfigDetails?.TransactionExchangeConfigDestinationTransactionAmountTypeID || amountTypeList[0]?.id
    );
  }, []);

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ p: 3 }}>
            <Box
              sx={{
                display: 'grid',
                columnGap: 2,
                rowGap: 3,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
              }}
            >
              <RHFTextField name="TransactionExchangeConfigLabel" type="text" label="عنوان" />

              <GeneralSelectMenu
                label="ارز مبدا"
                name="TransactionExchangeConfigSourceTransactionAmountTypeID"
                options={sourceAmountTypes}
                value={selectedSourceAmountType}
                onChangeValue={setSelectedSourceAmountType}
              />

              <GeneralDriverTextFiled
                name="TransactionExchangeConfigRate"
                type="text"
                label="میزان ارزش"
                helperDesc={`میزان ارزش ارز مبدا نسبت به ارز مقصد `}
              />

              <GeneralSelectMenu
                label="ارز مقصد"
                name="TransactionExchangeConfigDestinationTransactionAmountTypeID"
                options={destinationAmountTypes}
                value={selectedDestinationAmountType}
                onChangeValue={setSelectedDestinationAmountType}
              />
            </Box>

            <div className="flex items-center gap-4 mt-6">
              <LoadingButton type="submit" variant="contained" name="save" loading={isSubmitting}>
                ذخیره
              </LoadingButton>
              <LoadingButton type="submit" variant="contained" color="info" name="saveAndContinue" loading={isSubmitting}>
                {isEditMode ? 'ذخیره و ادامه ویرایش' : 'ذخیره و ایجاد بعدی'}
              </LoadingButton>
              <Button onClick={closeModal} variant="contained" color="error">
                انصراف
              </Button>
            </div>
          </Box>
        </Grid>
      </Grid>
    </FormProvider>
  );
};

export default ExchangeForm;
